import { useEffect, useState, useCallback } from "react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { useAuth } from "@/context/AuthContext";
import { api, formatCzk, formatDate, formatErr } from "@/lib/api";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { StatusBadge } from "@/components/StatusBadge";
import { Sparkles, Plus, Trophy, Upload, Camera, Heart, Building2 } from "lucide-react";
import { AddressAutocomplete } from "@/components/AddressAutocomplete";
import { resizeImage } from "@/lib/image";
import { toast } from "sonner";

const PREFS = [
  { key: "no_softener", label: "Bez aviváže" },
  { key: "hypoallergenic", label: "Hypoalergenní" },
  { key: "eco", label: "Eco praní" },
  { key: "strong_scent", label: "Silná vůně" },
  { key: "mild_scent", label: "Jemná vůně" },
  { key: "separate_colors", label: "Oddělit barvy" },
  { key: "low_temp", label: "Nízká teplota" },
];

const CustomerDashboard = () => {
  const { user, refresh } = useAuth();
  const [orders, setOrders] = useState([]);
  const [form, setForm] = useState({
    kg: "5",
    service_type: "standard",
    express_type: "none",
    pickup_address: user?.address || "",
    pickup_city: user?.city || "",
    pickup_zip: user?.zip || "",
    pickup_phone: user?.phone || "",
    pickup_time: "",
    notes: "",
    photo_pickup_bag: "",
    preferences: [],
    donation_amount: 0,
  });
  const [pricing, setPricing] = useState({ total: 0, per_kg: 60, commission: 0, provider_payout: 0 });
  const [submitting, setSubmitting] = useState(false);

  const loadOrders = useCallback(async () => {
    try {
      const { data } = await api.get("/orders/mine");
      setOrders(data);
    } catch (e) {
      console.error("Failed to load orders:", e);
      toast.error(formatErr(e));
    }
  }, []);

  useEffect(() => { loadOrders(); }, [loadOrders]);

  // Recalc pricing
  useEffect(() => {
    const fetchP = async () => {
      try {
        const { data } = await api.get(`/pricing/calculate?kg=${form.kg || 0}&service_type=${form.service_type}&express_type=${form.express_type}`);
        setPricing(data);
      } catch (e) {
        console.error("Failed to fetch pricing:", e);
      }
    };
    fetchP();
  }, [form.kg, form.service_type, form.express_type]);

  const togglePref = (k) =>
    setForm((f) => ({
      ...f,
      preferences: f.preferences.includes(k) ? f.preferences.filter((x) => x !== k) : [...f.preferences, k],
    }));

  const handlePhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const dataUrl = await resizeImage(file);
      setForm((f) => ({ ...f, photo_pickup_bag: dataUrl }));
    } catch (err) {
      toast.error(err.message || "Nepodařilo se nahrát foto");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = { ...form, kg: parseFloat(form.kg) };
      const { data: order } = await api.post("/orders", payload);
      toast.success("Objednávka vytvořena – čekáme, až ji prádelna přijme. Platba proběhne až poté.");
      // Reset form + refresh list; user stays on the dashboard and sees the order in "Přehled".
      setForm({ ...form, notes: "", photo_pickup_bag: "", preferences: [], donation_amount: 0 });
      await loadOrders();
      // Switch to orders tab so the customer can see the new order immediately.
      if (order?.id) {
        // Best-effort tab switch — the Tabs widget listens to `defaultValue` only,
        // so we scroll to it as a fallback signal.
        const el = document.querySelector('[data-testid="cust-tab-orders"]');
        if (el) el.click();
      }
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="container-page py-10">
        <div className="grid lg:grid-cols-12 gap-6">
          {/* Header summary */}
          <div className="lg:col-span-12 grid md:grid-cols-3 gap-4">
            <Card className="p-5 flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-sky-100 text-sky-700 grid place-items-center">
                <Sparkles className="w-5 h-5" />
              </div>
              <div>
                <div className="text-xs text-slate-500">Aktivní objednávky</div>
                <div className="font-display text-2xl font-bold text-slate-900" data-testid="cust-stat-active">
                  {orders.filter((o) => !["completed", "cancelled"].includes(o.status)).length}
                </div>
              </div>
            </Card>
            <Card className="p-5 flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-amber-100 text-amber-700 grid place-items-center">
                <Trophy className="w-5 h-5" />
              </div>
              <div>
                <div className="text-xs text-slate-500">Věrnostní body</div>
                <div className="font-display text-2xl font-bold text-slate-900" data-testid="cust-stat-points">
                  {user?.loyalty_points || 0}
                </div>
              </div>
            </Card>
            <Card className="p-5 flex items-center gap-4 bg-rose-50/40 border-rose-100">
              <div className="w-12 h-12 rounded-xl bg-rose-100 text-rose-600 grid place-items-center">
                <Heart className="w-5 h-5" />
              </div>
              <div>
                <div className="text-xs text-rose-700">Darováno na praní pro seniory</div>
                <div className="font-display text-2xl font-bold text-slate-900" data-testid="cust-stat-donated">
                  {formatCzk(user?.donations_given || 0)}
                </div>
              </div>
            </Card>
          </div>

          <Card className="lg:col-span-12 p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 bg-gradient-to-br from-sky-50 to-indigo-50 border-sky-200">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl bg-sky-600 text-white grid place-items-center">
                <Building2 className="w-5 h-5" />
              </div>
              <div>
                <div className="font-display font-bold text-slate-900">Máte více apartmánů nebo penzion?</div>
                <div className="text-sm text-slate-600">Přidejte více adres, naplánujte pravidelné svozy a stahujte měsíční přehledy.</div>
              </div>
            </div>
            <Link to="/apartmany" className="cta-primary text-sm py-2 px-5" data-testid="cust-apartments-cta">Otevřít správu apartmánů</Link>
          </Card>

          <Tabs defaultValue="new" className="lg:col-span-12">
            <TabsList className="grid w-full sm:w-auto sm:inline-grid grid-cols-2">
              <TabsTrigger value="new" data-testid="cust-tab-new"><Plus className="w-4 h-4 mr-1.5" /> Nová objednávka</TabsTrigger>
              <TabsTrigger value="orders" data-testid="cust-tab-orders">Moje objednávky ({orders.length})</TabsTrigger>
            </TabsList>

            {/* New order form */}
            <TabsContent value="new" className="mt-6">
              <Card className="p-7">
                <h2 className="font-display text-2xl font-bold text-slate-900">Vytvořit objednávku</h2>
                <p className="text-sm text-slate-500 mt-1">Vyplňte detaily, prádelny ve vašem okolí dostanou nabídku.</p>
                <form onSubmit={submit} className="mt-6 grid md:grid-cols-2 gap-5">
                  <div>
                    <Label>Počet kilogramů <span className="text-xs text-slate-400">(min. 5 kg)</span></Label>
                    <Input type="number" min="5" step="0.5" required value={form.kg} onChange={(e) => setForm({ ...form, kg: e.target.value })} data-testid="order-kg" />
                  </div>
                  <div>
                    <Label>Typ služby</Label>
                    <Select value={form.service_type} onValueChange={(v) => setForm({ ...form, service_type: v })}>
                      <SelectTrigger data-testid="order-service-type"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="standard">Standard (60 Kč/kg) – praní + složení</SelectItem>
                        <SelectItem value="premium">Premium (80 Kč/kg) – praní + složení + žehlení</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Expres</Label>
                    <Select value={form.express_type} onValueChange={(v) => setForm({ ...form, express_type: v })}>
                      <SelectTrigger data-testid="order-express-type"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Bez expresu (do 48 h)</SelectItem>
                        <SelectItem value="24h">Expres 24 h (+20 Kč/kg)</SelectItem>
                        <SelectItem value="same_day">Same-day (+40 Kč/kg)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Termín vyzvednutí</Label>
                    <Input type="datetime-local" required value={form.pickup_time} onChange={(e) => setForm({ ...form, pickup_time: e.target.value })} data-testid="order-time" />
                  </div>

                  <div className="md:col-span-2">
                    <Label>Adresa</Label>
                    <AddressAutocomplete
                      value={form.pickup_address}
                      onChange={(v) => setForm({ ...form, pickup_address: v })}
                      onPick={(p) => setForm({
                        ...form,
                        pickup_address: p.road || p.display,
                        pickup_city: p.city || form.pickup_city,
                        pickup_zip: p.postcode || form.pickup_zip,
                      })}
                      data-testid="order-address"
                    />
                  </div>
                  <div>
                    <Label>Město</Label>
                    <Input required value={form.pickup_city} onChange={(e) => setForm({ ...form, pickup_city: e.target.value })} data-testid="order-city" />
                  </div>
                  <div>
                    <Label>PSČ</Label>
                    <Input value={form.pickup_zip} onChange={(e) => setForm({ ...form, pickup_zip: e.target.value })} data-testid="order-zip" />
                  </div>
                  <div className="md:col-span-2">
                    <Label>Telefon</Label>
                    <Input required value={form.pickup_phone} onChange={(e) => setForm({ ...form, pickup_phone: e.target.value })} data-testid="order-phone" />
                  </div>

                  <div className="md:col-span-2">
                    <Label>Preference praní</Label>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {PREFS.map((p) => (
                        <button
                          type="button" key={p.key}
                          onClick={() => togglePref(p.key)}
                          data-testid={`pref-${p.key}`}
                          className={`px-3 py-1.5 rounded-full text-sm border transition-all ${form.preferences.includes(p.key) ? "bg-sky-600 text-white border-sky-600" : "bg-white text-slate-700 border-slate-200 hover:border-sky-300"}`}
                        >
                          {p.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="md:col-span-2">
                    <Label>Poznámka pro prádelnu</Label>
                    <Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="např. zvonek 4B, mám psa…" data-testid="order-notes" />
                  </div>

                  <div className="md:col-span-2 rounded-2xl border-2 border-dashed border-rose-200 bg-rose-50/40 p-4">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-full bg-rose-100 text-rose-600 grid place-items-center flex-shrink-0">
                        <Heart className="w-5 h-5" />
                      </div>
                      <div className="flex-1">
                        <div className="font-semibold text-slate-900">Daruji praní pro seniora nebo samoživitelku</div>
                        <p className="text-xs text-slate-600 mt-0.5">Z každé darované koruny pereme prádlo lidem, kteří si placené praní nemohou dovolit. Sbíráme do společného fondu.</p>
                        <div className="mt-3 flex flex-wrap gap-2">
                          {[0, 50, 100, 200].map((v) => (
                            <button
                              type="button" key={v}
                              onClick={() => setForm({ ...form, donation_amount: v })}
                              data-testid={`donation-${v}`}
                              className={`px-4 py-1.5 rounded-full text-sm border transition-all ${form.donation_amount === v ? "bg-rose-500 text-white border-rose-500" : "bg-white text-slate-700 border-slate-200 hover:border-rose-300"}`}
                            >
                              {v === 0 ? "Bez daru" : `+${v} Kč`}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="md:col-span-2">
                    <Label>Fotografie pytle s prádlem (volitelné)</Label>
                    <div className="mt-2 flex items-center gap-4">
                      <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-dashed border-slate-300 hover:border-sky-400 text-sm text-slate-600">
                        <Upload className="w-4 h-4" /> Nahrát foto
                        <input type="file" accept="image/*" className="hidden" onChange={handlePhoto} data-testid="order-photo" />
                      </label>
                      {form.photo_pickup_bag && (
                        <img src={form.photo_pickup_bag} alt="náhled" className="w-16 h-16 rounded-lg object-cover border border-slate-200" />
                      )}
                    </div>
                  </div>

                  <div className="md:col-span-2 grid sm:grid-cols-2 gap-4 items-end mt-2">
                    <div className="bg-sky-50 border border-sky-200 rounded-xl p-4">
                      <div className="text-xs text-sky-700 font-semibold uppercase tracking-wider">Vaše cena</div>
                      <div className="font-display text-3xl font-extrabold text-slate-900" data-testid="order-total">
                        {formatCzk(pricing.total + (form.donation_amount || 0))}
                      </div>
                      <div className="text-xs text-slate-500">
                        {formatCzk(pricing.total)} praní {form.donation_amount > 0 && <span className="text-rose-600">+ {formatCzk(form.donation_amount)} dar</span>}
                      </div>
                      <div className="text-xs text-slate-400 mt-1">{pricing.per_kg} Kč/kg • Provize 15 %: {formatCzk(pricing.commission)}</div>
                    </div>
                    <Button type="submit" size="lg" disabled={submitting} data-testid="order-submit">
                      {submitting ? "Vytvářím objednávku…" : "Vytvořit objednávku"}
                    </Button>
                  </div>
                </form>
              </Card>
            </TabsContent>

            <TabsContent value="orders" className="mt-6">
              {orders.length === 0 ? (
                <Card className="p-10 text-center text-slate-500">
                  Zatím žádné objednávky. <span className="text-slate-700 font-semibold">Vytvořte první!</span>
                </Card>
              ) : (
                <div className="grid gap-3">
                  {orders.map((o) => (
                    <Link key={o.id} to={`/order/${o.id}`} className="block group" data-testid={`order-row-${o.id}`}>
                      <Card className="p-5 flex items-center justify-between gap-4 hover:border-sky-300 transition-all">
                        <div className="flex items-center gap-4">
                          <div className="w-11 h-11 rounded-xl bg-slate-100 grid place-items-center text-slate-600">
                            <Camera className="w-5 h-5" />
                          </div>
                          <div>
                            <div className="font-semibold text-slate-900">
                              {o.kg} kg · {o.service_type === "premium" ? "Premium" : "Standard"}
                              {o.express_type !== "none" && <span className="ml-1 text-xs text-amber-600">{o.express_type === "24h" ? "Expres 24h" : "Same-day"}</span>}
                            </div>
                            <div className="text-xs text-slate-500">{formatDate(o.created_at)} · {o.pickup_city}</div>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          {o.status === "awaiting_payment" && o.payment_status !== "paid" && (
                            <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-200" data-testid={`order-pay-now-${o.id}`}>
                              💳 Zaplaťte nyní
                            </span>
                          )}
                          {o.status === "created" && (
                            <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 border border-slate-200" data-testid={`order-awaiting-provider-${o.id}`}>
                              Čeká na prádelnu
                            </span>
                          )}
                          <StatusBadge status={o.status} />
                          <div className="font-display font-bold text-slate-900">{formatCzk(o.total_price)}</div>
                        </div>
                      </Card>
                    </Link>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default CustomerDashboard;
